import { Schema } from "effect";
import { plainIdentityData } from "./identity-input.ts";

import { DevelopmentIdentifiers } from "./development-identifiers.ts";
import { schemaParser } from "./schema-compat.ts";

export const AGENTIC_TRACE_PHASES = [
  "queue",
  "dependency-install",
  "bundle",
  "load",
  "run",
  "reviewer",
  "cleanup"
] as const;
export const AgenticTracePhase = /* @__PURE__ */ (() => Schema.Literals(AGENTIC_TRACE_PHASES))();
export type AgenticTracePhase = typeof AgenticTracePhase.Type;

export const AGENTIC_CACHE_RESULTS = [
  "hit",
  "miss",
  "bypass",
  "corrupt"
] as const;
export const AgenticCacheResult = /* @__PURE__ */ (() => Schema.Literals(AGENTIC_CACHE_RESULTS))();
export type AgenticCacheResult = typeof AgenticCacheResult.Type;

export const AgenticTraceContextInput = /* @__PURE__ */ (() => Schema.Struct({
  changeId: Schema.String.pipe(Schema.optional),
  taskId: Schema.String.pipe(Schema.optional),
  runId: Schema.String.pipe(Schema.optional),
  parentRunId: Schema.String.pipe(Schema.optional),
  attemptId: Schema.String.pipe(Schema.optional),
  worktreeId: Schema.String.pipe(Schema.optional),
  /** Generic tree identity retained for compatibility with existing traces. */
  treeId: Schema.String.pipe(Schema.optional),
  inputTreeId: Schema.String.pipe(Schema.optional),
  outputTreeId: Schema.String.pipe(Schema.optional),
  candidateId: Schema.String.pipe(Schema.optional),
  checkId: Schema.String.pipe(Schema.optional),
  reviewId: Schema.String.pipe(Schema.optional),
  namespaceId: Schema.String.pipe(Schema.optional),
  fence: Schema.Number.pipe(Schema.optional),
  cacheResult: AgenticCacheResult.pipe(Schema.optional),
  eventSequence: Schema.Number.pipe(Schema.optional),
  targetRef: Schema.String.pipe(Schema.optional),
  phase: AgenticTracePhase.pipe(Schema.optional),
}))();
export type AgenticTraceContextInput = typeof AgenticTraceContextInput.Type;

export const AgenticTraceAttributes = /* @__PURE__ */ (() => Schema.Record(Schema.String, Schema.Union([Schema.String, Schema.Number])))();
export type AgenticTraceAttributes = typeof AgenticTraceAttributes.Type;

export const AGENTIC_TRACE_CONTEXT_FIELDS = [
  "changeId",
  "taskId",
  "runId",
  "parentRunId",
  "attemptId",
  "worktreeId",
  "treeId",
  "inputTreeId",
  "outputTreeId",
  "candidateId",
  "checkId",
  "reviewId",
  "namespaceId",
  "fence",
  "cacheResult",
  "eventSequence",
  "targetRef",
  "phase"
] as const satisfies readonly (keyof AgenticTraceContextInput)[];

const TARGET_REF_RE = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,127}$/;
const RefName = /* @__PURE__ */ (() => Schema.String.check(
  Schema.makeFilter(value => TARGET_REF_RE.test(value) && !value.includes("//") &&
    !value.split("/").some(part => part === "." || part === ".."),
    { message: "Trace target ref must be a bounded safe ref name." })
))();
const TraceInteger = (label: string, minimum: number) => Schema.Number.check(
  Schema.isInt({ message: `${label} must be a safe integer >= ${minimum}.` }),
  Schema.isGreaterThanOrEqualTo(minimum, { message: `${label} must be a safe integer >= ${minimum}.` })
);

/** The normalized correlation contract uses the same domain identities as RPC. */
export const AgenticTraceContext = /* @__PURE__ */ (() => Schema.Struct({
  ...AgenticTraceContextInput.fields,
  changeId: Schema.optional(DevelopmentIdentifiers.change),
  taskId: Schema.optional(DevelopmentIdentifiers.task),
  runId: Schema.optional(DevelopmentIdentifiers.run),
  parentRunId: Schema.optional(DevelopmentIdentifiers.run),
  attemptId: Schema.optional(DevelopmentIdentifiers.attempt),
  worktreeId: Schema.optional(DevelopmentIdentifiers.worktree),
  treeId: Schema.optional(DevelopmentIdentifiers.tree),
  inputTreeId: Schema.optional(DevelopmentIdentifiers.tree),
  outputTreeId: Schema.optional(DevelopmentIdentifiers.tree),
  candidateId: Schema.optional(DevelopmentIdentifiers.candidate),
  checkId: Schema.optional(DevelopmentIdentifiers["check-run"]),
  reviewId: Schema.optional(DevelopmentIdentifiers["review-run"]),
  namespaceId: Schema.optional(DevelopmentIdentifiers["runtime-namespace"]),
  fence: Schema.optional(TraceInteger("Trace fence", 1)),
  eventSequence: Schema.optional(TraceInteger("Trace event sequence", 0)),
  cacheResult: Schema.optional(AgenticCacheResult.annotate({ message: "Agentic trace cache result is invalid." })),
  phase: Schema.optional(AgenticTracePhase.annotate({ message: "Agentic trace phase is invalid." })),
  targetRef: Schema.optional(RefName)
}))();
export type AgenticTraceContext = typeof AgenticTraceContext.Type;

// Read descriptors before Schema can read fields. Accessors, inherited data and
// unknown/symbol keys are rejected without invoking a getter. Reflective proxy
// failures become Schema issues rather than escaping the decoding boundary.
export const AgenticTraceContextData = /* @__PURE__ */ (() => plainIdentityData(
  AGENTIC_TRACE_CONTEXT_FIELDS, "Agentic trace context"
).pipe(Schema.decodeTo(AgenticTraceContext)))();
export class AgenticTraceContextError extends Schema.TaggedError<AgenticTraceContextError>()(
  "AgenticTraceContextError", { message: Schema.String, status: Schema.tag(400) }
) {}
const contextContract = /* @__PURE__ */ (() => schemaParser(AgenticTraceContextData, error => new AgenticTraceContextError({ message: error.message })))();

/** Snapshot validated, bounded correlation so aliases cannot mutate telemetry. */
export const normalizeAgenticTraceContext = (input: AgenticTraceContextInput): AgenticTraceContext =>
  Object.freeze(contextContract(input));

/** Build exact bounded correlation attributes before telemetry export. */
export function agenticTraceAttributes(
  input: AgenticTraceContextInput
): AgenticTraceAttributes {
  return traceAttributesFromContext(normalizeAgenticTraceContext(input));
}

/** Pure projection for callers that have already decoded the context Schema. */
export function traceAttributesFromContext(context: AgenticTraceContext): AgenticTraceAttributes {
  return Object.freeze({
    ...(context.changeId === undefined
      ? {}
      : { "workerstation.change_id": context.changeId }),
    ...(context.taskId === undefined
      ? {}
      : { "workerstation.task_id": context.taskId }),
    ...(context.runId === undefined
      ? {}
      : { "workerstation.run_id": context.runId }),
    ...(context.parentRunId === undefined
      ? {}
      : { "workerstation.parent_run_id": context.parentRunId }),
    ...(context.attemptId === undefined
      ? {}
      : { "workerstation.attempt_id": context.attemptId }),
    ...(context.worktreeId === undefined
      ? {}
      : { "workerstation.worktree_id": context.worktreeId }),
    ...(context.treeId === undefined
      ? {}
      : { "workerstation.tree_id": context.treeId }),
    ...(context.inputTreeId === undefined
      ? {}
      : { "workerstation.input_tree_id": context.inputTreeId }),
    ...(context.outputTreeId === undefined
      ? {}
      : { "workerstation.output_tree_id": context.outputTreeId }),
    ...(context.candidateId === undefined
      ? {}
      : { "workerstation.candidate_id": context.candidateId }),
    ...(context.checkId === undefined
      ? {}
      : { "workerstation.check_id": context.checkId }),
    ...(context.reviewId === undefined
      ? {}
      : { "workerstation.review_id": context.reviewId }),
    ...(context.namespaceId === undefined
      ? {}
      : { "workerstation.namespace_id": context.namespaceId }),
    ...(context.fence === undefined
      ? {}
      : { "workerstation.fence": context.fence }),
    ...(context.cacheResult === undefined
      ? {}
      : { "workerstation.cache_result": context.cacheResult }),
    ...(context.eventSequence === undefined
      ? {}
      : { "workerstation.event_sequence": context.eventSequence }),
    ...(context.targetRef === undefined
      ? {}
      : { "workerstation.target_ref": context.targetRef }),
    ...(context.phase === undefined
      ? {}
      : { "workerstation.phase": context.phase })
  });
}
